import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useParams } from 'react-router';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { ANY_SPORT_MY_CLUB_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useDeeplinkFromNativeApp } from '@sorare/core/src/hooks/useDeeplinkFromNativeApp';
import { useUpdateSportProfile } from '@sorare/core/src/hooks/useUpdateSportProfile';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import { ClubEditor } from '#src/components/profile/Header/ClubEditor/index.tsx';

import type {
  ClubEditorPageQuery,
  ClubEditorPageQueryVariables,
} from './__generated__/index.graphql.ts';

const CLUB_EDITOR_PAGE_QUERY = gql`
  query ClubEditorPageQuery($slug: String!, $sport: Sport!) {
    user(slug: $slug) {
      slug
      userSportProfile: anyUserSportProfile(sport: $sport) {
        id
        ...ClubEditor_userSportProfile
      }
    }
  }
  ${ClubEditor.fragments.userSportProfile}
` as TypedDocumentNode<ClubEditorPageQuery, ClubEditorPageQueryVariables>;

export const ClubEditorPage = ({ sport }: { sport: Sport }) => {
  const { slug = '' } = useParams();
  const { isMe } = useCurrentUserContext();
  const { navigateWithDeeplink } = useDeeplinkFromNativeApp();
  const updateSportProfile = useUpdateSportProfile();

  const { data, loading } = useQuery(CLUB_EDITOR_PAGE_QUERY, {
    variables: { slug, sport },
    skip: !isMe(slug) || !slug,
  });

  if (!isMe(slug)) return null;
  if (loading) return <LoadingIndicator fullHeight />;
  if (!data?.user.userSportProfile) return null;

  return (
    <ClubEditor
      open
      onClose={() =>
        navigateWithDeeplink(
          generateSportPath(ANY_SPORT_MY_CLUB_SLUG, {
            sport,
            params: { slug },
          })
        )
      }
      onSubmit={async ({
        clubName,
        selectedStatistics,
        backgroundId,
        clubBadgeId,
        achievementBadgesIds,
      }) =>
        updateSportProfile({
          sport,
          clubName,
          selectedStatistics,
          backgroundId,
          clubBadgeId,
          achievementBadgesIds,
        })
      }
      userSportProfile={data.user.userSportProfile}
    />
  );
};
